import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaChevronDown, FaSearch } from "react-icons/fa";
import { usePortalPosition } from "../TaskManagement/usePortalPosition";
import {
  FILTER_CHEVRON_CLASS,
  FILTER_DROPDOWN_LI_RESET,
  FILTER_DROPDOWN_LIST_STYLE,
  FILTER_LABEL_CLASS,
  FILTER_TRIGGER_CLASS,
  FILTER_TRIGGER_OPEN_CLASS,
  getDropdownOptionStyle,
} from "../reports/commissions/commissionsConstants";

function AccountsNameMultiSelect({
  label = "Name",
  value = [],
  onChange,
  options = [],
  placeholder = "All names",
  searchPlaceholder = "Search users...",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const portalStyle = usePortalPosition(triggerRef, open, {
    placement: "bottom",
    gap: 6,
  });

  const normalizedOptions = useMemo(
    () =>
      options.map((opt) => ({
        value: opt.value ?? opt.id,
        label: opt.label ?? opt.name ?? String(opt.value ?? opt.id),
      })),
    [options],
  );

  const selectedSet = useMemo(
    () => new Set((value || []).map((item) => String(item.value))),
    [value],
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [normalizedOptions, search]);

  const triggerLabel = useMemo(() => {
    if (!value?.length) return placeholder;
    if (value.length === 1) return value[0].label;
    return `${value.length} names selected`;
  }, [value, placeholder]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inContainer && !inMenu) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setSearch("");
  }, [open]);

  const toggleOption = (option) => {
    const key = String(option.value);
    const exists = selectedSet.has(key);
    if (exists) {
      onChange((value || []).filter((item) => String(item.value) !== key));
    } else {
      onChange([...(value || []), { label: option.label, value: option.value }]);
    }
  };

  const menu = (
    <div
      ref={menuRef}
      className="tailwind-container z-[9999] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
      style={{
        ...portalStyle,
        minWidth: triggerRef.current?.offsetWidth || 220,
      }}
    >
      <div className="border-b border-gray-100 bg-[#FAFBFC] p-2">
        <div className="relative">
          <FaSearch
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            size={11}
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-[34px] w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-xs text-gray-900 outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      <ul className="max-h-52 overflow-y-auto py-1" style={FILTER_DROPDOWN_LIST_STYLE}>
        {filteredOptions.length === 0 ? (
          <li className="px-3 py-2 text-center text-xs text-gray-400" style={FILTER_DROPDOWN_LI_RESET}>
            {normalizedOptions.length === 0 ? "Loading users..." : "No users found"}
          </li>
        ) : (
          filteredOptions.map((option) => {
            const selected = selectedSet.has(String(option.value));
            return (
              <li key={option.value} style={FILTER_DROPDOWN_LI_RESET}>
                <button
                  type="button"
                  onClick={() => toggleOption(option)}
                  className="flex w-full items-center justify-between gap-2 text-xs transition"
                  style={getDropdownOptionStyle(selected)}
                  onMouseEnter={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "#F9FAFB";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span className="truncate">{option.label}</span>
                  {selected && <FaCheck size={10} className="shrink-0 text-[#007BFF]" />}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );

  return (
    <div className="min-w-0 flex-1 basis-0" ref={containerRef}>
      {label && <label className={FILTER_LABEL_CLASS}>{label}</label>}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((p) => !p)}
          className={`${FILTER_TRIGGER_CLASS} ${
            open ? FILTER_TRIGGER_OPEN_CLASS : ""
          } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        >
          <span
            className={`truncate ${value?.length ? "text-gray-800" : "text-gray-400"}`}
          >
            {triggerLabel}
          </span>
          <FaChevronDown
            size={10}
            className={`${FILTER_CHEVRON_CLASS} ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && !disabled && createPortal(menu, document.body)}
      </div>
    </div>
  );
}

export default AccountsNameMultiSelect;
